import React from 'react';
import { Trash2, Package, Truck } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useStore } from '../../context/StoreContext';
import { storefrontDisclosure, commissionDisclosureEnabled } from '../../utilities/complianceCopy';
import { useStorefrontTheme } from '../../shared/theme/storefrontTheme';
import Button from '../../shared/components/atoms/Button';
import QtyStepper from '../../shared/components/atoms/QtyStepper';
import logoPng from '../../assets/images/logo-png.png';
import {
  cartGroups,
  cartTotals,
  cartItemCount,
  hasUnavailable,
  unitPrice,
  wasDiscounted,
  isProductUnavailable,
  stockCeiling,
  quantityPayload,
  money,
} from './cartModel';
import './cart.css';

/* THE CART PAGE — grouped by brand, because that is how it ships.
   Ported from SP-main/cart.html and the `.ct-*` rules.

   The grouping is the point, not a layout choice: there is no warehouse, so
   every brand ships its own parcel on its own terms, each group carries its own
   shipping cost and dispatch time, and the order total is the sum of the
   parcels. cart.html quotes the product owner: "in cart it needs to be organized
   by brand since each brand is shipping to the customer".

   No `PageLayout` — it wraps children in `max-w-7xl mx-auto` and renders its own
   breadcrumb and heading, while `.br-wrap` is 1240px with a 40px gutter and owns
   both. Same call as /all-products, /brands/:id and /collections.

   The arithmetic is not here: `cartModel.js` owns it, so this page, the drawer
   and checkout cannot disagree about what the shopper is paying. */

const CartFullView = () => {
  const {
    cartItems, addToCart, removeCartProduct, pageDetails, storeDetails,
  } = useStore();
  const navigate = useNavigate();
  const sfxRef = useStorefrontTheme(pageDetails?.page_data?.colorAndApperances);

  const groups = cartGroups(cartItems);
  const totals = cartTotals(cartItems);
  const count = cartItemCount(cartItems);
  const blocked = hasUnavailable(cartItems);

  const setQuantity = (group, item, qty) => {
    const ceiling = stockCeiling(item);
    if (ceiling != null && qty > ceiling) {
      toast.error('Stock limit reached', { autoClose: 3000 });
      return;
    }
    addToCart(quantityPayload(group.raw, item, qty));
  };

  const removeLine = (item) => removeCartProduct({ id: item.id });

  const lineImage = (item) =>
    item?.product_variants?.image
    || item?.products?.product_images?.find((img) => img?.is_main === '1')?.src
    || item?.products?.product_images?.[0]?.src
    || logoPng;

  /* ---- one parcel ---- */
  const renderGroup = (group, i) => (
    <section className="ct-group" key={group.key}>
      <header className="ct-ghead">
        <div className="ct-gbrand">
          <span className="ct-gmark">
            <Package aria-hidden="true" />
          </span>
          <span>
            {/* "Ships by X", not the bare brand name: the group's job is to say
                who is sending this parcel. */}
            <span className="ct-gname">Ships by {group.name}</span>
            <span className="ct-gparcel">
              Parcel {i + 1} of {groups.length}
              {group.eta ? ` · ${group.eta}` : ''}
            </span>
          </span>
        </div>
        {/* One shipping statement per brand, in the header. This group used to
            end in a per-brand subtotal footer, so one $5.00 appeared three times
            per brand. ⛔ Do not reinstate a per-brand subtotal row. */}
        <span className={`ct-gship${group.isFree ? ' is-free' : ''}`}>
          {group.shipNote}
        </span>
      </header>

      <div className="ct-lines">
        {group.items.map((item) => {
          const gone = isProductUnavailable(group.raw, item);
          const title = item?.products?.title || '';
          const variant = item?.product_variants?.title;
          const href = `/product/${item?.products?.id}`;
          return (
            <div className="ct-line" key={item.id}>
              <Link className="ct-thumb" to={href}>
                <img src={lineImage(item)} alt="" loading="lazy" />
              </Link>

              <div className="ct-info">
                <Link className="ct-pname" to={href}>{title}</Link>
                <div className="ct-unit">
                  {money(unitPrice(item))} each
                  {variant && variant !== 'Default Title' ? ` · ${variant}` : ''}
                </div>
                {item?.customizable_note && (
                  <div className="ct-unit">Customised: {item.customizable_note}</div>
                )}
              </div>

              <QtyStepper
                small
                value={item.cart_quantity}
                max={stockCeiling(item)}
                disabled={gone}
                itemLabel={title}
                onChange={(qty) => setQuantity(group, item, qty)}
                onDecrementAtMin={() => removeLine(item)}
              />

              <div className="ct-lprice">
                {wasDiscounted(item) && (
                  <span className="ct-lwas">
                    {money(parseFloat(item.product_variants.msrp_price) * item.cart_quantity)}
                  </span>
                )}
                {money(unitPrice(item) * item.cart_quantity)}
              </div>

              <button
                className="ct-rm"
                type="button"
                onClick={() => removeLine(item)}
                aria-label={`Remove ${title}`}
              >
                <Trash2 aria-hidden="true" />
              </button>

              {gone && (
                <p className="ct-gone">
                  This is no longer available — remove it to check out.
                </p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );

  /* ---- the summary ----
     No tax number, and the total says "Estimated": tax without a delivery
     address is a guess, and a guess that reads high is itself a reason people
     abandon. Shipping stays a real number — it is the same per-brand figure
     checkout charges. ⛔ Never render tax as $0.00: that turns "we cannot know
     yet" into "no tax is due". */
  const summary = (
    <aside className="ct-sum">
      <h2>Order summary</h2>
      <div className="ct-srow">
        <span>Subtotal</span>
        <span>{money(totals.subtotal)}</span>
      </div>
      <div className="ct-srow">
        <span>Shipping</span>
        <span>{totals.shipping ? money(totals.shipping) : 'Free'}</span>
      </div>
      <div className="ct-srow is-total">
        <span>Estimated total</span>
        <span>{money(totals.total)}</span>
      </div>

      {/* Commission / seller disclosure, next to the checkout action. */}
      <p className="ct-disclosure">
        {storefrontDisclosure(storeDetails?.store_name, commissionDisclosureEnabled(storeDetails))}
      </p>

      <Button
        variant="primary"
        className="ct-go"
        onClick={() => navigate('/checkout')}
        disabled={blocked || totals.parcels === 0}
      >
        Checkout
      </Button>
      <button type="button" className="ct-keep" onClick={() => navigate('/all-products')}>
        Continue shopping
      </button>


    </aside>
  );

  return (
    <div ref={sfxRef} className="sfx">
      <div className="br-wrap">
        <nav className="br-crumb" aria-label="Breadcrumb">
          <Link to="/">Home</Link>
          {' / '}
          <span>Cart</span>
        </nav>

        <h1 className="br-h1">
          Cart
          {count > 0 && (
            <span className="br-h1n"> {count} item{count === 1 ? '' : 's'}</span>
          )}
        </h1>

        {groups.length === 0 ? (
          /* The empty cart is a real screen: it says what to do next and links
             to the two places a shopper actually wants. */
          <div className="br-empty ct-empty">
            <h2>Your cart is empty</h2>
            <p>
              Nothing in here yet. Everything we carry is worth a look — or start
              from one of our collections.
            </p>
            <div className="ct-emptybtns">
              <Button variant="primary" onClick={() => navigate('/all-products')}>
                Shop all products
              </Button>
              <Button variant="ghost" onClick={() => navigate('/all-collections')}>
                Browse collections
              </Button>
            </div>
          </div>
        ) : (
          <div className="ct-cols">
            <div className="ct-items">
              {/* Only when there is a split to explain. */}
              {totals.parcels > 1 && (
                <p className="ct-split">
                  <Package aria-hidden="true" />
                  <span>
                    This order arrives in{' '}
                    <span className="ct-strong">{totals.parcels} parcels</span>
                    {' '}— each brand ships its own, so they may not turn up on the
                    same day.
                  </span>
                </p>
              )}
              {groups.map(renderGroup)}
            </div>
            {summary}
          </div>
        )}
      </div>
    </div>
  );
};

export default CartFullView;
